import { LightningIcon } from '@phosphor-icons/react'
import type { DistributiveOmit } from '@tanstack/react-query'
import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { match } from 'ts-pattern'
import { Routes } from '../routes.ts'
import { Repository } from '../utils/api-client/api-schemas'
import * as styles from './repository-header.css'
import { BranchTag } from './tags/branch-tag'
import { SCMIcon } from './third-party-branding/scm-icon'
import { TooltipWithHover } from './tooltip'

type RepositoryHeaderProps = {
  repository?: DistributiveOmit<Repository, '_links'>
  showDefaultBranchTag?: boolean
  hasWorkflows?: boolean
  onWorkflowClick?: () => void
  /** See `RepositoryNameProps.isPageTitle` — only true where the repository is the page's subject. */
  isPageTitle?: boolean
}

export const RepositoryHeader: React.FC<RepositoryHeaderProps> = ({
  repository,
  showDefaultBranchTag = true,
  hasWorkflows,
  onWorkflowClick,
  isPageTitle = false,
}) => {
  const [isTruncated, setIsTruncated] = useState(false)
  const textRef = useRef<HTMLParagraphElement>(null)

  const displayName = getRepositoryDisplayName(repository)

  useEffect(() => {
    const element = textRef.current
    if (element) {
      setIsTruncated(element.scrollWidth > element.clientWidth)
    }
  }, [displayName.name])

  if (!repository) {
    return <div style={{ visibility: 'hidden' }}></div>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '12px' }}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {'owner' in displayName && <p className={styles.accountLoginText}>{displayName.owner}</p>}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
            <SCMIcon type={repository.type} size="large" withAriaLabel={true} />
            <RepositoryName
              repositoryId={repository.id}
              displayName={displayName.name}
              isTruncated={isTruncated}
              textRef={textRef}
              isPageTitle={isPageTitle}
            />
          </div>

          {showDefaultBranchTag && <BranchTag branch={repository.default_branch} />}
          {hasWorkflows && onWorkflowClick && (
            <TooltipWithHover
              trigger={
                <button
                  type="button"
                  className={styles.workflowIconButton}
                  onClick={onWorkflowClick}
                  aria-label="Manage workflows"
                >
                  <LightningIcon className={styles.workflowIcon} size={20} weight="fill" />
                </button>
              }
            >
              <span>Manage workflows</span>
            </TooltipWithHover>
          )}
        </div>
      </div>
    </div>
  )
}

export type RepositoryNameProps = {
  repositoryId?: string
  displayName: string
  /**
   * Renders the name as the page's `h1` rather than a paragraph.
   *
   * Off by default because this component also names rows in `scans-list`, where the repository is one
   * item among many rather than the subject of the page — a heading there would put a dozen `h1`s in the
   * outline. Opt in only where the repository *is* what the page is about.
   */
  isPageTitle?: boolean
} & (
  | {
      isTruncated: false
      textRef?: undefined
    }
  | {
      isTruncated: boolean
      textRef: React.RefObject<HTMLParagraphElement | null>
    }
)

export const RepositoryName: React.FC<RepositoryNameProps> = ({
  repositoryId,
  displayName,
  isTruncated,
  textRef,
  isPageTitle = false,
}) => {
  const NameElement = isPageTitle ? 'h1' : 'p'
  const repositoryNameParagraph = (
    <NameElement ref={textRef} className={styles.repositoryName}>
      {displayName}
    </NameElement>
  )

  const wrappedRepositoryName = repositoryId?.trim() ? (
    <Link
      to={Routes.RepositoryDetailsPage.createPath({ repositoryId })}
      style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer', display: 'inline-block' }}
      onClick={e => e.stopPropagation()}
    >
      {repositoryNameParagraph}
    </Link>
  ) : (
    repositoryNameParagraph
  )

  if (isTruncated) {
    return (
      <TooltipWithHover trigger={wrappedRepositoryName}>
        <span>{displayName}</span>
      </TooltipWithHover>
    )
  }

  return wrappedRepositoryName
}

type RepositoryNameDisplay = { owner?: string; name: string }
const getRepositoryDisplayName = (repository?: DistributiveOmit<Repository, '_links'>): RepositoryNameDisplay => {
  return match(repository)
    .with(undefined, () => {
      return { owner: undefined, name: '' }
    })
    .with({ type: 'git' }, repository => {
      return { owner: undefined, name: repository.name }
    })
    .with({ type: 'github' }, repository => {
      return { owner: repository.owner, name: repository.name }
    })
    .with({ type: 'gitlab' }, repository => {
      return { owner: repository.namespace, name: repository.name }
    })
    .with({ type: 'azure' }, repository => {
      return { owner: repository.organization + '/' + repository.project, name: repository.name }
    })
    .with({ type: 'bitbucket' }, repository => {
      return { owner: repository.workspace, name: repository.name }
    })
    .exhaustive()
}
